import _ from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import { Card, Progress, Spin } from "antd";
import classNames from "classnames";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { STEPS, TIME_LOADING, RANGE_LOADING, WAIT_FOR_DOWNLOAD_STATUS_TIMEOUT } from "config/loader";
import useCommonStore from "stores/useCommon";
import { HomePreload } from "@/services/api/type/mock.type";
import API from "services/api";

interface Props {
  skipHomePreload: boolean;
  homePreload: HomePreload;
  isFetchedHomePreload: boolean;
  id?: string;
}

const Loader: FC<Props> = ({ skipHomePreload, homePreload, id }) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const pollingRef = useRef<NodeJS.Timeout>();
  const startTimeRef = useRef<number>(0);
  const { setIsDoneLoader } = useCommonStore();
  const [firstStepStatus, setFirstStepStatus] = useState<"pending" | "ok" | "timeout" | "error">("pending");

  const [steps, setSteps] = useState(
    STEPS.map((step, index) => {
      const baseTimeout =
        TIME_LOADING + TIME_LOADING * Number(_.sample(RANGE_LOADING)! / 100);

      // Make the first 4 steps a bit longer, the remaining steps shorter
      const factor = index <= 3 ? 1.5 : 0.5;
      const timeout = Math.max(200, Math.floor(baseTimeout * factor));

      return {
        step,
        timeout,
        color: "",
        noIcon: false,
      };
    })
  );
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (
      !_.isEmpty(homePreload) &&
      homePreload.message &&
      !skipHomePreload &&
      currentStep === 4
    ) {
      const timeoutPreload =
        TIME_LOADING + TIME_LOADING * Number(_.sample(RANGE_LOADING)! / 100);
      const preload = {
        step: homePreload.message,
        timeout: timeoutPreload,
        color: "#FF9B04",
        noIcon: true,
      };

      const newSteps = [...steps];
      newSteps.splice(4, 0, preload);

      setSteps(newSteps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipHomePreload, homePreload, currentStep]);

  // Poll context_status for the 3rd step (index 2)
  useEffect(() => {
    if (currentStep !== 2 || !id) return;

    startTimeRef.current = Date.now();

    const pollStatus = async () => {
      try {
        const elapsed = Date.now() - startTimeRef.current;
        if (elapsed >= WAIT_FOR_DOWNLOAD_STATUS_TIMEOUT) {
          // Timeout: mark as orange and move to next
          setFirstStepStatus("timeout");
          setSteps((prev) => {
            const newSteps = [...prev];
            newSteps[2] = { ...newSteps[2], color: "#FF9B04" };
            return newSteps;
          });
          setCurrentStep(3);
          return;
        }

        const response = await API.mock.getHomeContextStatus(id);
        if (response.download_status === "ok") {
          setFirstStepStatus("ok");
          setCurrentStep(3);
        } else if (response.download_status === "hard_refresh") {
          // Force hard refresh - bypass cache
          window.location.href = window.location.href;
        } else if (response.download_status === "retried") {
          // Mark as orange and keep polling
          setSteps((prev) => {
            const newSteps = [...prev];
            newSteps[2] = { ...newSteps[2], color: "#FF9B04" };
            return newSteps;
          });
          pollingRef.current = setTimeout(pollStatus, 1000);
        } else {
          // Keep polling
          pollingRef.current = setTimeout(pollStatus, 1000);
        }
      } catch {
        // Error: mark as orange and move to next
        setFirstStepStatus("error");
        setSteps((prev) => {
          const newSteps = [...prev];
          newSteps[2] = { ...newSteps[2], color: "#412908" };
          return newSteps;
        });
        setCurrentStep(3);
      }
    };

    pollStatus();

    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [currentStep, id]);

  useEffect(() => {
    if (currentStep >= steps.length) {
      setIsDoneLoader(true);
      return;
    }

    // Skip timeout for step 2 - handled by polling
    if (currentStep === 2) return;

    timeoutRef.current = setTimeout(() => {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
    }, steps[currentStep]?.timeout);

    return () => clearTimeout(timeoutRef.current);
  }, [currentStep, setIsDoneLoader, steps]);

  return (
    <div className="energy-home__loader">
      <Card className="energy-home__project-card mb-3">
        <h2 className="title">Fiabilité en cours de traitement</h2>
        <Progress
          showInfo={false}
          percent={Math.floor((currentStep / steps.length) * 100)}
          strokeColor="#3B8686"
          trailColor="#CCCCCC"
        />

        <ul className="energy-home__loader__steps">
          {steps.map((item, index) => (
            <li
              key={index}
              className={classNames(
                index < currentStep ? "active" : "",
                item.noIcon && "!pl-5"
              )}
            >
              {index < currentStep ? (
                <>{!item.noIcon && <CheckOutlined />}</>
              ) : index === currentStep ? (
                <Spin
                  size="small"
                  indicator={<LoadingOutlined spin color="#3B8686" />}
                />
              ) : null}
              <p style={item.color ? { color: item.color } : {}}>{item.step}</p>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};

export default Loader;
